
import Vue from "vue";
import Breadcrump from "@/components/common/Breadcrump.vue";
import { hasPermission } from "@/composables/permissions";
import request from "@/utils/request";

interface VersionOption {
  id: number;
  title: string;
}

interface Devicestatus {
  version: string;
  deviceName: string;
  deviceOtaId: number;
}

interface VersionDetail {
  id: number;
  version: string;
  updateTitle: string;
  updateDesc: string;
  appFileInfo: string | null;
  batchFileInfo: string | null;
  resourceFileInfo: string | null;
  forceUpdate: boolean;
  isActive: boolean;
}

interface FileInfo {
  fullName: string;
  size: number;
  lastModified: string;
}

interface LatestLog {
  message: string;
  state: number;
  createdAt: Date;
  version: string;
}

export default Vue.extend({
  components: { Breadcrump },
  props: {
    deviceId: {
      type: [String, Number],
      required: true
    }
  },

  data() {
    return {
      valid: true,
      loading: false,
      errors: [] as string[],
      lastInstalledVersion: "",
      versionOptions: [] as VersionOption[],
      deviceStatus: null as Devicestatus | null,
      successMessage: "" as string,
      successTimeout: null as any,
      selectedVersionId: null as number | null,
      selectedVersion: null as VersionDetail | null,
      latestLog: null as LatestLog | null,
      form: {
        isActive: true
      },
      crumbs: [
        {
          text: "خانه",
          disabled: false,
          href: "/"
        },
        {
          text: "مجموعه ها",
          disabled: false,
          href: "/complexes"
        },
        {
          text: `مجموعه ${this.$store.state.selectedComplexName}`,
          disabled: false,
          href: `/complex-devices/${this.$store.state.selectedComplexId}`
        },
        {
          text: "آپدیت دستگاه",
          disabled: true
        }
      ]
    };
  },

  computed: {
    deviceIdNum(): number {
      return Number(this.deviceId);
    }
  },

  async mounted() {
    if (hasPermission("Ota_Show")) await this.loadDeviceVersion();
    if (hasPermission("OtaList_Show")) await this.loadVersions();
    if (hasPermission("OtaList_ShowLogs")) await this.loadLatestLog();
    if (this.deviceStatus?.deviceOtaId && hasPermission("Ota_Show")) {
      this.selectedVersionId = this.deviceStatus?.deviceOtaId;
      await this.loadSelectedVersion();
    }
  },

  methods: {
    formatFileSize(bytes: number): string {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },

    formatFileInfo(fileInfo?: FileInfo): string {
      if (!fileInfo) return "";
      return `${fileInfo.fullName} (${this.formatFileSize(fileInfo.size)}) - ${
        fileInfo.lastModified
      }`;
    },

    // متد جدید برای تبدیل کد وضعیت به متن فارسی
    getLogStatusText(state: number): string {
      switch (state) {
        case 1:
          return "آپدیت موفق";
        case 2:
          return "آپدیت ناموفق";
        case 3:
          return "بازیابی نسخه پیشین ناموفق بود";
        case 4:
          return "آپدیت دستی";
        default:
          return "وضعیت نامشخص";
      }
    },

    // متد فرمت تاریخ (اختیاری برای نمایش بهتر)
    formatDate(date: Date): string {
      if (!date) return "";
      return new Date(date).toLocaleString("fa-IR");
    },

    async loadVersions() {
      try {
        const res = await request.get("/deviceotalist/versions");
        this.versionOptions = res.data.result.map((v: any) => ({
          id: v.id,
          title: v.updateTitle
        }));
      } catch (err) {
        this.errors.push("خطا در بارگذاری نسخه‌ها");
      }
    },

    async loadLatestLog() {
      try {
        const res = await request.get(
          `/deviceotalist/${this.deviceId}/latest-log`
        );
        const data = res?.data?.result;
        if (data) {
          this.latestLog = {
            message: data.message,
            version: data.version,
            createdAt: data.createdAt,
            state: data.state
          };
        } else {
          this.latestLog = null;
        }
      } catch (err) {
        this.errors.push("خطا در بارگذاری اخرین گزارش نسخه");
      }
    },

    async loadSelectedVersion() {
      if (!this.selectedVersionId) {
        this.selectedVersion = null;
        return;
      }

      try {
        const res = await request.get(
          `/deviceotalist/${this.selectedVersionId}/latest`
        );
        const data = res.data.result;
        this.selectedVersion = {
          id: data.id,
          version: data.lastVersion,
          updateTitle: data.updateTitle,
          updateDesc: data.updateDesc,
          forceUpdate: data.forceUpdate,
          isActive: data.isActiveUpdate,
          appFileInfo: this.formatFileInfo(data.appFileInfo),
          batchFileInfo: this.formatFileInfo(data.batchFileInfo),
          resourceFileInfo: this.formatFileInfo(data.resourceFileInfo)
        };
      } catch {
        this.errors.push("خطا در بارگذاری جزئیات نسخه");
      }
    },

    async loadDeviceVersion() {
      try {
        const res = await request.get(
          `/deviceotalist/${this.deviceId}/current`
        );
        const data = res.data.result;
        this.deviceStatus = {
          deviceOtaId: data.deviceOtaId,
          version: data.version,
          deviceName: data.deviceName
        };
        this.lastInstalledVersion = this.deviceStatus.version;
        this.form.isActive = data.isActive;

        this.$set(this.crumbs, 3, {
          text: `آپدیت دستگاه ${this.deviceStatus.deviceName}`,
          disabled: true
        });
      } catch {
        this.$set(this.crumbs, 3, {
          text: `آپدیت دستگاه`,
          disabled: true
        });
      }
    },

    async submitForm() {
      this.errors = [];
      if (!this.selectedVersionId) {
        this.errors.push("لطفاً یک نسخه انتخاب کنید");
        return;
      }

      const formRef = this.$refs.form as any;
      if (!formRef.validate()) return;

      this.loading = true;
      try {
        const payload = {
          deviceId: this.deviceIdNum,
          otaListId: this.selectedVersionId,
          isActive: this.form.isActive
        };

        await request.post("/deviceotalist/assign", payload);
        this.successMessage = "نسخه با موفقیت به دستگاه تخصیص داده شد.";
        this.$nextTick(() => {
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
        });

        if (this.successTimeout) clearTimeout(this.successTimeout);
        this.successTimeout = setTimeout(() => {
          this.successMessage = "";
          this.$router.push(
            `/complex-devices/${this.$store.state.selectedComplexId}`
          );
        }, 2500);
      } catch (err) {
        this.errors.push(err.response?.data?.message || "خطا در ذخیره");
      } finally {
        this.loading = false;
      }
    }
  }
});
